import { TOrganizationTypes } from '@sravni/ugc/lib/organizations/types';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { createOrgMap } from '../../helpers/createOrgMap';
import type { IItem, IMenu } from '../../interfaces/organizations';
import { loadOrganizationsList } from '../../services/organizations';

export interface IState {
  menu: {
    [key: string]: IMenu[];
  };
  dependencyCounts: {
    [key: string]: Record<string, any>;
  };
  details: {
    data:
      | {
          [key: number]: IItem;
        }
      | {};
    infoAliasMap: Record<string, string>;
  };
  organizationsList: IItem[];
  organizationsMap: {
    [key: string]: IItem;
  };
  organizationsTotal: number;
  organizationsActiveTotal: number;
}

const initialState: IState = {
  menu: {},
  dependencyCounts: {},
  details: {
    data: {},
    infoAliasMap: {},
  },
  organizationsList: [],
  organizationsMap: {},
  organizationsTotal: null,
  organizationsActiveTotal: null,
};

const name = 'organizations';

const slice = createSlice({
  name,
  initialState,
  reducers: {
    setOrganizationsList(state, { payload }) {
      state.organizationsList = payload;
      state.organizationsMap = createOrgMap(payload);
      state.organizationsTotal = payload?.length ?? 0;
    },
    setOrganizationByAlias(state, { payload }) {
      state.details.data[payload.id] = payload;
      state.details.infoAliasMap[payload.alias] = `${payload.id}`;
    },
    setOrganizationRating(state, { payload }) {
      if (state.details.data[payload.id]) {
        state.details.data[payload.id].rating = payload.data;
      }
    },
    setOrganizationPosition(state, { payload }) {
      if (state.details.data[payload.id]) {
        state.details.data[payload.id].position = payload.data;
      }
    },
    setMenu: (state, { payload }) => {
      state.menu[payload?.alias] = payload?.menu;
      if (payload?.counts) {
        state.dependencyCounts[payload?.alias] = payload?.counts;
      }
    },
    setActiveOrganizationCount: (state, { payload }) => {
      state.organizationsActiveTotal = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOrganizationList.fulfilled, (state, { payload }) => {
      state.organizationsList = payload;
      state.organizationsMap = createOrgMap(payload);
      state.organizationsTotal = payload?.length ?? 0;
    });
    builder.addCase(fetchOrganizationList.rejected, (state) => {
      state.organizationsList = initialState.organizationsList;
      state.organizationsMap = initialState.organizationsMap;
      state.organizationsTotal = initialState.organizationsTotal;
    });
  },
});

const { reducer, actions } = slice;

export const {
  setOrganizationsList,
  setOrganizationByAlias,
  setOrganizationRating,
  setOrganizationPosition,
  setMenu,
  setActiveOrganizationCount,
} = actions;

export const fetchOrganizationList = createAsyncThunk<IItem[], TOrganizationTypes>(
  `${name}/fetchOrganizationList`,
  async (payload) => {
    return await loadOrganizationsList({ organizationType: payload });
  },
);

export default reducer;
